import TextField from "@mui/material/TextField";
import {MenuItem, Select, Stack} from "@mui/material";
import {Document, DocumentType} from "crqlar-react-common/dist/models/profile/document.model";
import * as React from "react";
import {useTranslation} from "react-i18next";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import {convertLocalDateToDate, formatDateLocalISO} from "crqlar-react-common/dist/utils/date.util";
import Property from "crqlar-react-common/dist/components/Property";

interface Props {
    document: Document
    setDocument: (document: Document) => void
    index: number
    disabled?: boolean
}

const DocumentField = (props: Props) => {

    const {t} = useTranslation()

    return (
        <Stack direction={'column'} gap={1} width={'100%'}>
            <Property title={t('Document ID')} labelAlignment={'top'}>
                <TextField
                    id={`document-id-${props.index}`}
                    data-testid={`document-id-${props.index}`}
                    size={'small'}
                    value={props.document.documentID || ''}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            documentID: e.target.value
                        })
                    }}
                    fullWidth
                    disabled={props.disabled}
                />
            </Property>

            <Property title={t('Document type')} labelAlignment={'top'}>
                <Select
                    id={`document-type-${props.index}`}
                    data-testid={`document-type-${props.index}`}
                    variant={'outlined'}
                    value={props.document.documentTypeC}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            documentTypeC: e.target.value as DocumentType
                        })
                    }}
                    size={'small'}
                    disabled={props.disabled}
                    fullWidth
                >
                    {
                        Object.values(DocumentType).map((type, index) => (
                            <MenuItem
                                key={index}
                                value={type}
                                id={`document-type-${type}`}
                            >
                                {type ? t(`Document Type ${type}`) : t('(Not set)')}
                            </MenuItem>
                        ))
                    }
                </Select>
            </Property>

            <Property title={t('Document type (source)')} labelAlignment={'top'}>
                <TextField
                    id={`document-doc-type-orig-${props.index}`}
                    data-testid={`document-doc-type-orig-${props.index}`}
                    size={'small'}
                    value={props.document.documentType || ''}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            documentType: e.target.value
                        })
                    }}
                    fullWidth
                    disabled={props.disabled}
                />
            </Property>

            <Property title={t('Document issue authority')} labelAlignment={'top'}>
                <TextField
                    id={`document-authority-${props.index}`}
                    data-testid={`document-authority-${props.index}`}
                    size={'small'}
                    value={props.document.docIssueAuthority || ''}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            docIssueAuthority: e.target.value
                        })
                    }}
                    fullWidth
                    disabled={props.disabled}
                />
            </Property>

            <Property title={t('Place of issue')} labelAlignment={'top'}>
                <TextField
                    id={`document-place-of-issue-${props.index}`}
                    data-testid={`document-place-of-issue-${props.index}`}
                    size={'small'}
                    value={props.document.placeOfIssue || ''}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            placeOfIssue: e.target.value
                        })
                    }}
                    fullWidth
                    disabled={props.disabled}
                />
            </Property>

            <Property title={t('Country of issue')} labelAlignment={'top'}>
                <TextField
                    id={`document-country-of-issue-${props.index}`}
                    data-testid={`document-country-of-issue-${props.index}`}
                    size={'small'}
                    value={props.document.countryOfIssue || ''}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            countryOfIssue: e.target.value
                        })
                    }}
                    fullWidth
                    disabled={props.disabled}
                />
            </Property>

            <Property title={t('Country of birth')} labelAlignment={'top'}>
                <TextField
                    id={`document-country-of-birth-${props.index}`}
                    data-testid={`document-country-of-birth-${props.index}`}
                    size={'small'}
                    value={props.document.birthCountry || ''}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            birthCountry: e.target.value
                        })
                    }}
                    fullWidth
                    disabled={props.disabled}
                />
            </Property>

            <Property title={t('Effective Date')} labelAlignment={'top'}>
                <CDatePicker
                    value={convertLocalDateToDate(props.document?.effectiveDate)}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            effectiveDate: formatDateLocalISO(e)
                        })
                    }}
                    disabled={props.disabled}
                    dateOnly
                />
            </Property>

            <Property title={t('Expiration Date')} labelAlignment={'top'}>
                <CDatePicker
                    value={convertLocalDateToDate(props.document?.expirationDate)}
                    onChange={(e) => {
                        props.setDocument({
                            ...props.document,
                            expirationDate: formatDateLocalISO(e)
                        })
                    }}
                    disabled={props.disabled}
                    dateOnly
                />
            </Property>
        </Stack>
    )
}

export default DocumentField;
